import { useCallback } from "react";

import type {
	OptionPmsItemFromDirectCommentInput,
	OptionPmsItemFromReservationCommentInput,
} from "@/lib/gql/__generated__/graphql";
import { Option_Pms_Item_Type } from "@/lib/gql/__generated__/graphql";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import * as Repository from "../../../_repository";

import type * as Type from "../type";

type Props = {
	option: Repository.Query.QueryData.OptionPmsList.Option;
	viewModel: Type.ViewModel;
};

export const useUpdateMemo = ({ option, viewModel }: Props) => {
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();

	const { onUpdateOptionPmsItemFromReservationComment: _onUpdateOptionPmsItemFromReservationComment } =
		Repository.Mutation.UseCase.useOptionPmsItemFromReservationUpdateCommentMutation();
	/**
	 * 通常予約時のメモの更新
	 */
	const onUpdateOptionPmsItemFromReservationComment = useCallback(async () => {
		if (!option.reservationID || !option.uniqueID) throw new Error("予期しないオプションです");

		/*
		 * 通常予約から
		 */
		const input: OptionPmsItemFromReservationCommentInput = {
			comment: viewModel.memo.value,
			hotelID: option.hotelID,
			reservationID: option.reservationID,
			uniqueID: option.uniqueID,
		} satisfies OptionPmsItemFromReservationCommentInput;

		return await _onUpdateOptionPmsItemFromReservationComment(input);
	}, [
		_onUpdateOptionPmsItemFromReservationComment,
		option.hotelID,
		option.reservationID,
		option.uniqueID,
		viewModel.memo,
	]);

	const { onUpdateOptionPmsItemFromDirectComment: _onUpdateOptionPmsItemFromDirectComment } =
		Repository.Mutation.UseCase.useOptionPmsItemFromDirectUpdateCommentMutation();
	/**
	 * 直接予約時のメモの更新
	 */
	const onUpdateOptionPmsItemFromDirectComment = useCallback(async () => {
		if (!option.cardID || !option.date || !option.optionID) throw new Error("予期しないオプションです");

		/**
		 * 直接予約から
		 */
		const input: OptionPmsItemFromDirectCommentInput = {
			cardID: option.cardID,
			comment: viewModel.memo.value,
			date: option.date,
			hotelID: option.hotelID,
			optionID: option.optionID,
			subOptionID: option.subOptionID,
		} satisfies OptionPmsItemFromDirectCommentInput;

		return await _onUpdateOptionPmsItemFromDirectComment(input);
	}, [
		_onUpdateOptionPmsItemFromDirectComment,
		option.cardID,
		option.date,
		option.hotelID,
		option.optionID,
		option.subOptionID,
		viewModel.memo.value,
	]);

	/**
	 * メモの更新
	 */
	const onUpdateMemo = useCallback(
		async (successCallback: () => void) => {
			const callback = async () => {
				if (!option.itemType || !option.hotelID) throw new Error("予期しないオプションです");

				return option.itemType === Option_Pms_Item_Type.FromReservation
					? await onUpdateOptionPmsItemFromReservationComment()
					: await onUpdateOptionPmsItemFromDirectComment();
			};

			showLoadingScreenCallback();

			await callback()
				.then(({ errors }) => {
					if (!errors) {
						notifyUserEventInfo({ variant: "default", title: "メモを更新しました" });
						/**
						 * ダイアログを閉じる
						 */
						successCallback();
					}
				})
				.finally(() => hideLoadingScreenCallback());
		},
		[
			hideLoadingScreenCallback,
			onUpdateOptionPmsItemFromDirectComment,
			onUpdateOptionPmsItemFromReservationComment,
			option.hotelID,
			option.itemType,
			showLoadingScreenCallback,
		],
	);

	return {
		// メモの更新
		onUpdateMemo,
	};
};
